// Phase COTTON-RM-002 Part C — Cotton RM Control Dashboard.
//
// 25-card management view rolled up by the backend dashboard service.
// No client-side fakery: when a card has no underlying data, it shows
// 0 with a quiet tone rather than synthesising numbers.

import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Row, Select, Skeleton, Space, Statistic,
  Tag, Typography,
} from 'antd'
import {
  AlertOutlined, CheckCircleOutlined, ClockCircleOutlined,
  ExperimentOutlined, FireOutlined, InboxOutlined,
  QrcodeOutlined, ReloadOutlined, SafetyCertificateOutlined,
  SwapOutlined, WarningOutlined, DollarOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import apiClient from '@/shared/api/client'
import { cottonRmApiPartC, parakhApi } from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

interface KpiCard {
  title: string
  value: number
  precision?: number
  prefix?: string
  suffix?: string
  to?: string
  tone?: 'ok' | 'warn' | 'danger' | 'info'
  icon?: React.ReactNode
  hint?: string
}

function toneColor(tone?: KpiCard['tone']) {
  if (tone === 'warn')   return '#faad14'
  if (tone === 'danger') return '#ff4d4f'
  if (tone === 'ok')     return '#52c41a'
  if (tone === 'info')   return '#1677ff'
  return undefined
}

function KpiTile({ kpi }: { kpi: KpiCard }) {
  const body = (
    <Card size="small" hoverable={!!kpi.to} style={{ height: '100%' }}>
      <Statistic
        title={
          <Space size={4}>
            {kpi.icon}
            <span>{kpi.title}</span>
          </Space>
        }
        value={kpi.value}
        precision={kpi.precision}
        prefix={kpi.prefix}
        suffix={kpi.suffix}
        valueStyle={{ color: toneColor(kpi.tone), fontSize: 22 }}
      />
      {kpi.hint && <Text type="secondary" style={{ fontSize: 12 }}>{kpi.hint}</Text>}
    </Card>
  )
  return kpi.to ? <Link to={kpi.to} style={{ display: 'block', height: '100%' }}>{body}</Link> : body
}


export default function CottonRmDashboardPage() {
  const [plantId, setPlantId] = useState<string | undefined>()

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-rm-dash'],
    queryFn: async () =>
      (await apiClient.get<{ id: string; plant_name: string }[]>('/plants')).data,
  })

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['cotton-rm-dashboard', plantId],
    queryFn: () => cottonRmApiPartC.dashboard(plantId),
    refetchInterval: 60_000,
  })

  // Phase COTTON-PARAKH-001 — pull Parakh counters alongside the dashboard
  const { data: parakhCount } = useQuery({
    queryKey: ['parakh-in-qc', plantId],
    queryFn: () => parakhApi.parakhInQcCount(plantId),
    refetchInterval: 60_000,
  })
  const { data: divergentList = [] } = useQuery({
    queryKey: ['parakh-divergence-count', plantId],
    queryFn: () => parakhApi.listDivergent(plantId),
    refetchInterval: 60_000,
  })

  if (isLoading) return <div style={{ padding: 16 }}><Skeleton active /></div>
  if (!data) return <Empty description="Dashboard not available." />

  const c = data.cards

  // Group 1 — Gate / Premises
  const gateCards: KpiCard[] = [
    { title: 'Trucks in Premises', value: c.trucks_in_premises,
      icon: <SwapOutlined />, tone: 'info',
      to: '/inventory/cotton-in-premises',
      hint: 'Gate-entered, not yet GRN-accepted' },
    { title: 'Tare Pending', value: c.tare_pending,
      icon: <ClockCircleOutlined />, tone: c.tare_pending > 0 ? 'warn' : undefined,
      hint: 'Awaiting tare weighbridge' },
    { title: 'Provisional Stock', value: c.provisional_kg, suffix: ' kg',
      precision: 0, icon: <InboxOutlined />, tone: 'info' },
    { title: 'Provisional Value', value: c.provisional_value, prefix: '₹',
      precision: 0, icon: <DollarOutlined />, tone: 'info',
      hint: c.data_not_available_value_count > 0
        ? `${c.data_not_available_value_count} entries missing rate` : '' },
  ]

  // Group 2 — QC pipeline
  const qcCards: KpiCard[] = [
    { title: 'QC Pending Bales', value: c.qc_pending,
      icon: <ExperimentOutlined />, tone: c.qc_pending > 0 ? 'warn' : undefined,
      to: '/quality/cotton-qc-pending' },
    { title: 'Moisture Pending', value: c.moisture_pending,
      icon: <ExperimentOutlined />, tone: c.moisture_pending > 0 ? 'warn' : undefined,
      to: '/quality/cotton-qc-pending' },
    { title: 'HVI Pending', value: c.hvi_pending,
      icon: <ExperimentOutlined />, tone: c.hvi_pending > 0 ? 'warn' : undefined,
      to: '/quality/cotton-qc-pending' },
    { title: 'QC Hold', value: c.qc_hold,
      icon: <WarningOutlined />, tone: c.qc_hold > 0 ? 'danger' : undefined,
      to: '/quality/cotton-qc-pending' },
    { title: 'Rejected', value: c.rejected_count,
      icon: <AlertOutlined />, tone: c.rejected_count > 0 ? 'danger' : undefined },
  ]

  // Group 3 — Accepted inventory
  const stockCards: KpiCard[] = [
    { title: 'Accepted Bales (in stock)', value: c.accepted_count,
      icon: <CheckCircleOutlined />, tone: 'ok' },
    { title: 'Accepted Weight', value: c.accepted_kg, suffix: ' kg',
      precision: 0, icon: <InboxOutlined />, tone: 'ok' },
    { title: 'Ready for Mixing', value: c.ready_count,
      icon: <FireOutlined />, tone: 'ok',
      to: '/inventory/mixing-readiness' },
    { title: 'Ready Weight', value: c.ready_kg, suffix: ' kg',
      precision: 0, icon: <FireOutlined />, tone: 'ok' },
  ]

  // Group 4 — Commercial states
  const flowCards: KpiCard[] = [
    { title: 'Reserved', value: c.reserved_count,
      icon: <SafetyCertificateOutlined />, tone: 'info' },
    { title: 'Reserved Weight', value: c.reserved_kg, suffix: ' kg',
      precision: 0, icon: <SafetyCertificateOutlined />, tone: 'info' },
    { title: 'Issued', value: c.issued_count,
      icon: <SwapOutlined />, tone: 'info' },
    { title: 'Issued Weight', value: c.issued_kg, suffix: ' kg',
      precision: 0, icon: <SwapOutlined />, tone: 'info' },
    { title: 'Consumed', value: c.consumed_count,
      icon: <FireOutlined />, tone: undefined },
    { title: 'Consumed Weight', value: c.consumed_kg, suffix: ' kg',
      precision: 0, icon: <FireOutlined /> },
  ]

  // Group 5 — Gaps / hygiene
  const gapCards: KpiCard[] = [
    { title: 'Missing Location', value: c.missing_location,
      icon: <WarningOutlined />, tone: c.missing_location > 0 ? 'warn' : undefined,
      hint: 'Accepted bales without godown' },
    { title: 'Missing Cost', value: c.missing_cost,
      icon: <WarningOutlined />, tone: c.missing_cost > 0 ? 'warn' : undefined,
      hint: 'Accepted bales without effective ₹/kg' },
    { title: 'QR Missing', value: c.qr_missing,
      icon: <QrcodeOutlined />, tone: c.qr_missing > 0 ? 'warn' : undefined,
      to: '/inventory/bales/qr-print' },
    { title: 'QR Printed, Not Scanned', value: c.qr_printed_not_scanned,
      icon: <QrcodeOutlined />, tone: c.qr_printed_not_scanned > 0 ? 'info' : undefined },
  ]

  // Group 6 — Cost adjustment + restatement
  const costCards: KpiCard[] = [
    { title: 'Pending Cost Adjustments', value: c.pending_cost_adjustments,
      icon: <DollarOutlined />, tone: c.pending_cost_adjustments > 0 ? 'warn' : undefined,
      to: '/inventory/cotton-cost-adjustments' },
    { title: 'Downstream Cost Warnings', value: c.downstream_cost_warnings,
      icon: <WarningOutlined />,
      tone: c.downstream_cost_warnings > 0 ? 'danger' : undefined,
      to: '/inventory/cotton-cost-adjustment-report' },
    { title: 'Restatements Pending Finance', value: c.restatement_pending_finance,
      icon: <DollarOutlined />,
      tone: c.restatement_pending_finance > 0 ? 'warn' : undefined,
      to: '/inventory/cotton-cost-restatements' },
  ]

  // Phase COTTON-PARAKH-001 — Parakh cards
  const parakhCards: KpiCard[] = [
    { title: 'Parakh-Procured GRNs in QC',
      value: parakhCount?.count ?? 0,
      icon: <ExperimentOutlined />,
      tone: (parakhCount?.count ?? 0) > 0 ? 'info' : undefined,
      to: '/quality/cotton-qc-pending',
      hint: 'Pre-certified by Parakh, mill QC pending' },
    { title: 'Parakh Divergence Alerts',
      value: divergentList.length,
      icon: <WarningOutlined />,
      tone: divergentList.length > 0 ? 'danger' : undefined,
      hint: 'Mill values diverge from Parakh promise' },
  ]

  const renderGroup = (label: string, cards: KpiCard[]) => (
    <>
      <Title level={5} style={{ marginBottom: 8, marginTop: 16 }}>{label}</Title>
      <Row gutter={[12, 12]}>
        {cards.map(k => (
          <Col key={k.title} xs={24} sm={12} md={8} lg={6} xl={4}>
            <KpiTile kpi={k} />
          </Col>
        ))}
      </Row>
    </>
  )

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Cotton RM Control Dashboard</Title>
      <Text type="secondary">
        Single-pane management view. Numbers are computed live from
        GateEntry / GRN / Bale / StockLedger / CottonCostAdjustment —
        no values are faked.
      </Text>

      <Space wrap style={{ marginTop: 12, marginBottom: 4 }}>
        <Select placeholder="All plants" allowClear style={{ width: 240 }}
                value={plantId} onChange={setPlantId}
                options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
        <Tag icon={<ReloadOutlined spin={isFetching} />}
              color={isFetching ? 'processing' : 'default'}
              onClick={() => refetch()}
              style={{ cursor: 'pointer' }}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Auto-refresh every 60s
        </Text>
      </Space>

      {c.data_not_available_value_count > 0 && (
        <Alert style={{ marginTop: 8 }} type="info" showIcon
                message={`${c.data_not_available_value_count} provisional entries are missing rate — value is partial.`} />
      )}

      {renderGroup('Gate / Premises', gateCards)}
      {renderGroup('QC Pipeline', qcCards)}
      {renderGroup('Accepted Inventory', stockCards)}
      {renderGroup('Commercial State', flowCards)}
      {renderGroup('Hygiene / Gaps', gapCards)}
      {renderGroup('Cost & Finance', costCards)}
      {renderGroup('Parakh', parakhCards)}
    </div>
  )
}
